import { useAppDispatch } from "../../../app/store";
import useTimerBasedState from "../../hooks/useTimerBasedState";
import { locationSelectors } from "../../store/slice/locationSlice";
import {
  userPlanActions,
  userPlanSelectors,
} from "../../store/slice/userPlanSlice";
import { ButtonLarge, ButtonMedium } from "../ui/buttons";
import ActionCard from "./ActionCard";
import MainActionCardButton from "./ActionCardButtons/MainActionCardButton";
import SkipActionModalButton from "./ActionCardButtons/SkipActionModalButton";
import ActionCardExtraActivityContainer from "./ActionCardExtraActivityContainer";
import { GranularActionOptions } from "./GranularActionOptions/GranularActionOptions";
import { ReactionAnimation } from "./ReactionAnimation";
import { createTranslationKey } from "./utils";
import { RegionalNamespaces } from "@pslifestyle/common/src/constants/translations";
import { GranularActionOption } from "@pslifestyle/common/src/schemas";
import {
  ActionImpact,
  WholeAction,
} from "@pslifestyle/common/src/types/planTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  action: WholeAction;
}

const DefaultRecommendationActionCard = ({
  action,
  onChooseActionClick,
  onAlreadyDoThisClick,
}: Props & {
  onChooseActionClick: () => void;
  onAlreadyDoThisClick: () => void;
}): JSX.Element | null => {
  const [showGranularActions, setShowGranularActions] = useState(false);
  const hasGranularActions =
    action.granularOptions && action.granularOptions.length > 0;

  const { t } = useTranslation([
    "recommendations",
    "common",
    RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
  ]);
  const dispatch = useAppDispatch();
  const country = useSelector(locationSelectors.country);
  if (!country) return null;
  const selectAction = (
    selectedGranularAction?: GranularActionOption,
    impact?: ActionImpact,
  ) => {
    dispatch(
      userPlanActions.addAction({
        actionId: action.id,
        ...(selectedGranularAction && impact
          ? { granularSelection: { option: selectedGranularAction, impact } }
          : {}),
      }),
    );
    onChooseActionClick();
  };
  const handleChooseThisAction = () => {
    if (hasGranularActions) {
      setShowGranularActions(true);
      return;
    }
    selectAction();
  };
  return (
    <div className="rounded-2xl">
      <ActionCard
        action={action}
        cyData={`recommendationActionCard.default.card.${action.id}`}
        showGranularActionPotential={hasGranularActions}
      >
        <ActionCardExtraActivityContainer displaySingleActivity={false}>
          <SkipActionModalButton action={action} />
          <ButtonMedium
            theme="secondary"
            className={`w-full justify-center !leading-4 ${hasGranularActions ? "hidden" : ""}`}
            disabled={hasGranularActions}
            buttonClassName="w-full"
            onClick={() => {
              dispatch(userPlanActions.addAlreadyDoThisAction(action.id));
              onAlreadyDoThisClick();
            }}
            cyData="recommendationActionCard.alreadyDoThis.button"
          >
            {t("iAlreadyDoThis")}
          </ButtonMedium>
        </ActionCardExtraActivityContainer>

        {showGranularActions ? (
          <GranularActionOptions
            actionId={action.id}
            granularOptions={action.granularOptions ?? []}
            onClose={() => setShowGranularActions(false)}
            onOptionSelect={selectAction}
            countryCode={country.code}
          />
        ) : (
          <MainActionCardButton
            theme="primary"
            onClick={handleChooseThisAction}
            cyData="recommendationActionCard.choose.button"
            icon={
              hasGranularActions
                ? { size: "medium", type: "ChevronDown", position: "left" }
                : undefined
            }
          >
            {t("chooseAction")}
          </MainActionCardButton>
        )}
      </ActionCard>
    </div>
  );
};
const SelectedRecommendationActionCard = ({
  action,
  onUnchooseActionClick,
  showAnimation,
}: Props & {
  onUnchooseActionClick: () => void;
  showAnimation: boolean;
}): JSX.Element | null => {
  const [showGranularActions, setShowGranularActions] = useState(false);

  const { t } = useTranslation([
    "recommendations",
    RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
  ]);
  const dispatch = useAppDispatch();
  const country = useSelector(locationSelectors.country);
  if (!country) return null;

  const selectAction = (
    selectedGranularAction: GranularActionOption,
    impact: ActionImpact,
  ) => {
    dispatch(
      userPlanActions.updateGranularActionSelection({
        actionId: action.id,
        selectedOption: selectedGranularAction,
        impact,
      }),
    );
  };

  const renderGranularOptions = () =>
    showGranularActions ? (
      <GranularActionOptions
        actionId={action.id}
        granularOptions={action.granularOptions ?? []}
        selectedOption={action.selectedGranularOption}
        countryCode={country.code}
        onClose={() => setShowGranularActions(false)}
        onOptionSelect={selectAction}
      />
    ) : (
      <ButtonLarge
        buttonClassName="mx-5 mb-3"
        className="w-full"
        theme="secondary"
        icon={{ size: "medium", type: "ChevronDown", position: "left" }}
        onClick={() => setShowGranularActions(true)}
      >
        {t(
          createTranslationKey(
            action.selectedGranularOption?.optionTranslationKey ?? "",
            country?.code,
          ),
          { ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS },
        )}
      </ButtonLarge>
    );
  return (
    <div className="rounded-2xl border-4 border-green-100">
      <ActionCard
        action={action}
        cyData={`recommendationActionCard.selected.card.${action.id}`}
      >
        <ReactionAnimation show={showAnimation} reactionType="heartColor" />
        {action.selectedGranularOption && renderGranularOptions()}
        <MainActionCardButton
          theme="goalCTA"
          icon={{ size: "medium", type: "Check", position: "left" }}
          onClick={() => {
            dispatch(userPlanActions.removeAction(action.id));
            onUnchooseActionClick();
          }}
        >
          {t("removeAction")}
        </MainActionCardButton>
      </ActionCard>
    </div>
  );
};

const AlreadyDoThisRecommendationActionCard = ({
  action,
  onUnchooseActionClick,
  showAnimation,
}: Props & {
  onUnchooseActionClick: () => void;
  showAnimation: boolean;
}): JSX.Element => {
  const { t } = useTranslation("recommendations");
  const dispatch = useAppDispatch();

  return (
    <div className="rounded-2xl border-4 border-green-140">
      <ActionCard
        action={action}
        cyData={`recommendationActionCard.alreadyDoThis.card.${action.id}`}
      >
        <ActionCardExtraActivityContainer displaySingleActivity>
          <ReactionAnimation show={showAnimation} reactionType="thumbUpColor" />
          <ButtonLarge
            theme="goalCTAActive"
            icon={{ size: "medium", type: "Check", position: "left" }}
            className="w-full justify-center !leading-4"
            buttonClassName="w-full"
            onClick={() => {
              dispatch(userPlanActions.removeAlreadyDoThisAction(action.id));
              onUnchooseActionClick();
            }}
          >
            {t("iAlreadyDoThis")}
          </ButtonLarge>
        </ActionCardExtraActivityContainer>
      </ActionCard>
    </div>
  );
};

const RecommendationActionCard = ({ action }: Props): JSX.Element => {
  useState<boolean>(false);
  const {
    state: actionChosenAnimationState,
    activateState: activateActionChosenAnimation,
    deactivateState: deactivateActionChosenAnimation,
  } = useTimerBasedState(5900);
  const {
    state: actionAlreadyDoThisAnimationState,
    activateState: activateActionAlreadyDoThisAnimation,
    deactivateState: deactivateActionAlreadyDoThisAnimation,
  } = useTimerBasedState(5900);

  const selectedActions = useSelector(userPlanSelectors.selectedActions);
  const alreadyDoThisActions = useSelector(
    userPlanSelectors.alreadyDoThisActions,
  );

  const selectedAction = selectedActions.find(
    (selectedAction) => selectedAction.id === action.id,
  );

  const isActionAlreadyDoThis = !!alreadyDoThisActions.find(
    (alreadyDoThisAction) => alreadyDoThisAction.id === action.id,
  );

  if (isActionAlreadyDoThis) {
    return (
      <AlreadyDoThisRecommendationActionCard
        action={action}
        onUnchooseActionClick={() => deactivateActionAlreadyDoThisAnimation}
        showAnimation={actionAlreadyDoThisAnimationState}
      />
    );
  }

  if (selectedAction) {
    return (
      <SelectedRecommendationActionCard
        action={selectedAction}
        onUnchooseActionClick={() => deactivateActionChosenAnimation}
        showAnimation={actionChosenAnimationState}
      />
    );
  }

  return (
    <DefaultRecommendationActionCard
      action={action}
      onChooseActionClick={() => activateActionChosenAnimation()}
      onAlreadyDoThisClick={() => activateActionAlreadyDoThisAnimation()}
    />
  );
};

export default RecommendationActionCard;
